/**
 * Activity Kidz - Visitor Profile Manager
 * Handles localStorage-based visitor profiling for personalization
 * 
 * Usage:
 * - On collection pages: AKZ.VisitorProfile.recordCategoryVisit('{{ collection.metafields.custom.image_category }}');
 * - On product pages: AKZ.MediaSorter.init();
 */

window.AKZ = window.AKZ || {};

// =============================================================================
// VISITOR PROFILE MODULE
// =============================================================================

AKZ.VisitorProfile = (function() {
  'use strict';

  const STORAGE_KEY = 'akz_visitor_profile';
  const SCHEMA_VERSION = 2;
  const MAX_SESSION_HISTORY = 20;

  function decodeHtmlEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  function getDefaultProfile() {
    return {
      version: SCHEMA_VERSION,
      categoryAffinity: {},
      currentSession: {
        entryCategory: null,
        startedAt: new Date().toISOString(),
        visitHistory: []
      },
      location: {
        lat: null,
        lng: null,
        accuracy: null,
        capturedAt: null,
        source: null
      },
      preferences: {
        ageGroup: null,
        birthday: null,
        birthdayChild: null
      },
      firstSeen: new Date().toISOString(),
      lastSeen: new Date().toISOString()
    };
  }

  function get() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return getDefaultProfile();
      }

      const profile = JSON.parse(stored);

      if (profile.version !== SCHEMA_VERSION) {
        return migrateProfile(profile);
      }

      return profile;
    } catch (e) {
      console.warn('[AKZ] Failed to parse visitor profile, resetting:', e);
      return getDefaultProfile();
    }
  }

  function save(profile) {
    try {
      profile.lastSeen = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
      return true;
    } catch (e) {
      console.error('[AKZ] Failed to save visitor profile:', e);
      return false;
    }
  }

  function migrateProfile(oldProfile) {
    const newProfile = getDefaultProfile();

    if (oldProfile.categoryAffinity) {
      newProfile.categoryAffinity = oldProfile.categoryAffinity;
    }
    if (oldProfile.firstSeen) {
      newProfile.firstSeen = oldProfile.firstSeen;
    }
    if (oldProfile.location) {
      newProfile.location = { ...newProfile.location, ...oldProfile.location };
    }
    if (oldProfile.preferences) {
      newProfile.preferences = { ...newProfile.preferences, ...oldProfile.preferences };
    }

    if (oldProfile.version === 1 && oldProfile.currentSession) {
      newProfile.currentSession.entryCategory = oldProfile.currentSession.entryCategory;
      newProfile.currentSession.startedAt = oldProfile.currentSession.startedAt;
    }

    save(newProfile);
    return newProfile;
  }

  function recordCategoryVisit(category) {
    category = decodeHtmlEntities(category); // Decode HTML entities like &amp;
    if (!category || typeof category !== 'string') {
      console.warn('[AKZ] recordCategoryVisit called with invalid category:', category);
      return;
    }

    const profile = get();
    const now = new Date().toISOString();
    const today = now.split('T')[0];

    if (!profile.categoryAffinity[category]) {
      profile.categoryAffinity[category] = {
        visits: 0,
        firstVisit: today,
        lastVisit: null,
        sessionVisits: 0
      };
    }

    const affinity = profile.categoryAffinity[category];

    affinity.visits += 1;
    affinity.lastVisit = today;
    affinity.sessionVisits = (affinity.sessionVisits || 0) + 1;

    if (!profile.currentSession.entryCategory) {
      profile.currentSession.entryCategory = category;
    }

    profile.currentSession.visitHistory.push({
      category: category,
      visitedAt: now
    });

    if (profile.currentSession.visitHistory.length > MAX_SESSION_HISTORY) {
      profile.currentSession.visitHistory = profile.currentSession.visitHistory.slice(-MAX_SESSION_HISTORY);
    }

    save(profile);

    console.debug('[AKZ] Recorded category visit:', category, affinity);
  }

  function getTopCategories(n) {
    const profile = get();
    const categories = Object.entries(profile.categoryAffinity);

    if (categories.length === 0) {
      return [];
    }

    const scored = categories.map(([category, data]) => {
      const daysSinceLastVisit = data.lastVisit
        ? Math.floor((Date.now() - new Date(data.lastVisit).getTime()) / (1000 * 60 * 60 * 24))
        : 999;

      const recencyMultiplier = Math.exp(-daysSinceLastVisit / 10);
      const score = data.visits * recencyMultiplier;

      return {
        category,
        score: Math.round(score * 100) / 100,
        visits: data.visits,
        lastVisit: data.lastVisit,
        sessionVisits: data.sessionVisits || 0
      };
    });

    scored.sort((a, b) => b.score - a.score);

    return n ? scored.slice(0, n) : scored;
  }

  function getPrimaryCategory() {
    const top = getTopCategories(1);
    return top.length > 0 ? top[0].category : null;
  }

  function getSessionEntryCategory() {
    const profile = get();
    return profile.currentSession.entryCategory;
  }

  function getLastSessionCategory() {
    const profile = get();
    const history = profile.currentSession.visitHistory;

    if (history.length === 0) {
      return null;
    }

    return history[history.length - 1].category;
  }

  function getSessionHistory() {
    const profile = get();
    return profile.currentSession.visitHistory;
  }

  function getSessionCategoriesByRecency() {
    const profile = get();
    const history = profile.currentSession.visitHistory;

    if (history.length === 0) {
      return [];
    }

    const seen = new Set();
    const result = [];

    for (let i = history.length - 1; i >= 0; i--) {
      const category = history[i].category;
      if (!seen.has(category)) {
        seen.add(category);
        result.push(category);
      }
    }

    return result;
  }

  function setPreference(key, value) {
    const profile = get();
    if (profile.preferences.hasOwnProperty(key)) {
      profile.preferences[key] = value;
      save(profile);
    } else {
      console.warn('[AKZ] Unknown preference key:', key);
    }
  }

  function getPreference(key) {
    const profile = get();
    return profile.preferences[key] || null;
  }

  function resetSession() {
    const profile = get();

    Object.keys(profile.categoryAffinity).forEach((category) => {
      profile.categoryAffinity[category].sessionVisits = 0;
    });

    profile.currentSession = {
      entryCategory: null,
      startedAt: new Date().toISOString(),
      visitHistory: []
    };

    save(profile);
  }

  function clear() {
    localStorage.removeItem(STORAGE_KEY);
    console.debug('[AKZ] Visitor profile cleared');
  }

  function debug() {
    return get();
  }

  return {
    get,
    save,
    recordCategoryVisit,
    getTopCategories,
    getPrimaryCategory,
    getSessionEntryCategory,
    getLastSessionCategory,
    getSessionHistory,
    getSessionCategoriesByRecency,
    setPreference,
    getPreference,
    resetSession,
    clear,
    debug
  };
})();


// =============================================================================
// MEDIA SORTER MODULE
// =============================================================================

AKZ.MediaSorter = (function() {
  'use strict';

  // =========================================================================
  // CATEGORY KEYWORDS - Based on collection.metafields.custom.image_category
  // These must match the exact category names from your Liquid code
  // =========================================================================
  const CATEGORY_KEYWORDS = {
    'Bowling Alley': [
      'bowling', 'bowl', 'lane', 'bowling lane', 'bowling alley', 'pins', 'strike'
    ],
    'Indoor Play Area': [
      'play area', 'indoor play', 'play zone', 'soft play', 'ball pit', 
      'slides', 'toddler', 'kids play', 'children play', 'playground'
    ],
    'Animal Petting Farm': [
      'animal', 'petting', 'farm', 'zoo', 'pet', 'rabbit', 'goat', 'bird',
      'feeding', 'animals'
    ],
    'Escape Rooms': [
      'escape', 'escape room', 'puzzle', 'mystery', 'clue', 'locked room'
    ],
    'Trampoline & Adventure': [
      'trampoline', 'jump', 'jumping', 'bounce', 'bouncing', 'foam pit',
      'foam blocks', 'aerial', 'flip', 'ninja', 'adventure', 'zip line',
      'zipline', 'rope', 'climbing wall'
    ],
    'VR Games': [
      'vr', 'virtual reality', 'headset', 'vr game', 'immersive', 
      'virtual reality headset', 'vr racing', 'simulator'
    ],
    'Arcade Games': [
      'arcade', 'arcade game', 'gaming', 'video game', 'air hockey', 
      'claw machine', 'racing game', 'ticket', 'redemption', 'pinball',
      'foosball', 'game zone', 'gaming zone'
    ],
    'Go Karting': [
      'go kart', 'karting', 'go-kart', 'kart', 'race track', 'racing kart'
    ],
    'Sports & Fitness': [
      'cricket', 'box cricket', 'batting', 'bowling cricket',
      'golf', 'mini golf', 'putting',
      'climbing', 'wall climbing', 'rock climbing', 'bouldering',
      'sports', 'fitness', 'obstacle', 'ninja warrior',
      'badminton', 'tennis', 'squash', 'basketball', 'football'
    ],
    'Laser Tag': [
      'laser', 'laser tag', 'laser gun', 'lazer', 'tag arena', 'combat'
    ]
  };

  // Default configuration
  const DEFAULT_CONFIG = {
    // Main gallery container selector
    galleryContainerSelector: '.product__media-list',
    
    // Thumbnail gallery selector (FIXED: use .thumbnail-list not .thumbnail-slider)
    thumbnailContainerSelector: '.thumbnail-list',
    
    // Individual media item selector
    mediaItemSelector: 'li',
    
    // Selectors to identify video items (FIXED: added deferred-media)
    videoElementSelectors: [
      'video',
      'iframe',
      'deferred-media',                    // Shopify lazy-load video component
      '[data-media-type="video"]',
      '.product__media-video'
    ],
    
    // Whether to sync main gallery and thumbnails
    syncGalleries: true,
    
    // Hide galleries during sort to prevent flash
    hideWhileSorting: true,
    
    // Debug mode - logs sorting decisions
    debug: false
  };

  let config = { ...DEFAULT_CONFIG };

  /**
   * Initialize the media sorter
   * @param {Object} customConfig - Override default configuration
   */
  function init(customConfig = {}) {
    config = { ...DEFAULT_CONFIG, ...customConfig };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => sortMedia());
    } else {
      sortMedia();
    }
  }

  /**
   * Main sorting function
   */
  function sortMedia() {
    const categoriesToTry = AKZ.VisitorProfile.getSessionCategoriesByRecency();

    if (categoriesToTry.length === 0) {
      log('No category history found, keeping default order');
      return;
    }

    log('Categories to try (by recency):', categoriesToTry);

    // Find both galleries
    const mainGallery = document.querySelector(config.galleryContainerSelector);
    const thumbGallery = document.querySelector(config.thumbnailContainerSelector);

    if (!mainGallery) {
      console.warn('[AKZ MediaSorter] Main gallery not found:', config.galleryContainerSelector);
      return;
    }

    // Get media items from main gallery
    const mainItems = Array.from(mainGallery.querySelectorAll(':scope > ' + config.mediaItemSelector));
    
    // Get thumbnail items (used as source of truth for alt text)
    const thumbItems = thumbGallery 
      ? Array.from(thumbGallery.querySelectorAll(':scope > ' + config.mediaItemSelector))
      : [];

    if (mainItems.length === 0) {
      log('No media items found in main gallery');
      return;
    }

    log('Found', mainItems.length, 'main gallery items and', thumbItems.length, 'thumbnails');

    // Parse media items - use thumbnails for alt text if available
    const parsedMedia = mainItems.map((item, index) => {
      // Get alt text from thumbnail first (it has better alt text), then fall back to main item
      let altText = '';
      
      if (thumbItems[index]) {
        altText = getAltTextFromElement(thumbItems[index]);
      }
      
      if (!altText) {
        altText = getAltTextFromElement(item);
      }

      const isVideo = isVideoItem(item);

      return {
        mainElement: item,
        thumbElement: thumbItems[index] || null,
        originalIndex: index,
        isVideo: isVideo,
        altText: altText
      };
    });

    log('Parsed media:');
    parsedMedia.forEach(m => {
      log(`  [${m.originalIndex}] ${m.isVideo ? '🎬 VIDEO' : '🖼️ IMAGE'} - "${m.altText.substring(0, 70)}${m.altText.length > 70 ? '...' : ''}"`);
    });

    // Try each category until we find matches
    let sortedMedia = null;
    let matchedCategory = null;

    for (const category of categoriesToTry) {
      const keywords = CATEGORY_KEYWORDS[category];
      if (!keywords) {
        log('No keywords defined for category:', category);
        continue;
      }

      log(`Trying category "${category}" with keywords:`, keywords.slice(0, 5).join(', ') + '...');

      const result = sortByKeywords(parsedMedia, keywords);
      
      if (result.hasMatches) {
        sortedMedia = result.sorted;
        matchedCategory = category;
        log(`✅ Found ${result.matchCount} matches for category:`, category);
        break;
      } else {
        log(`❌ No matches for category:`, category);
      }
    }

    if (!sortedMedia) {
      log('No matching media found for any category, keeping default order');
      return;
    }

    log('Applying sort for category:', matchedCategory);

    // Apply the sort to both galleries
    applySort(mainGallery, sortedMedia, 'mainElement');
    
    if (thumbGallery && config.syncGalleries) {
      applySort(thumbGallery, sortedMedia, 'thumbElement');
    }

    log('✅ Media sorting complete');
  }

  /**
   * Check if a media item is a video
   */
  function isVideoItem(item) {
    // Check each video selector
    for (const selector of config.videoElementSelectors) {
      if (item.matches && item.matches(selector)) {
        return true;
      }
      if (item.querySelector(selector)) {
        return true;
      }
    }
    
    // Also check class names for video indicators
    const className = item.className || '';
    if (className.includes('video') || className.includes('Video')) {
      return true;
    }

    return false;
  }

  /**
   * Get alt text from an element (checks multiple sources)
   */
  function getAltTextFromElement(item) {
    // Check all images within the item
    const images = item.querySelectorAll('img');
    for (const img of images) {
      if (img.alt && img.alt.trim()) {
        return img.alt.trim();
      }
    }

    // Check data-alt attribute on the item itself
    if (item.dataset && item.dataset.alt) {
      return item.dataset.alt;
    }

    // Check aria-label
    if (item.getAttribute('aria-label')) {
      return item.getAttribute('aria-label');
    }

    // Check for any element with data-alt
    const altElement = item.querySelector('[data-alt]');
    if (altElement && altElement.dataset.alt) {
      return altElement.dataset.alt;
    }

    return '';
  }

  /**
   * Sort media by keywords
   * Returns: { sorted: [...], hasMatches: boolean, matchCount: number }
   */
  function sortByKeywords(parsedMedia, keywords) {
    const matchingVideos = [];
    const matchingImages = [];
    const nonMatching = [];

    for (const media of parsedMedia) {
      const matches = matchesKeywords(media.altText, keywords);

      if (matches) {
        if (media.isVideo) {
          matchingVideos.push(media);
        } else {
          matchingImages.push(media);
        }
      } else {
        nonMatching.push(media);
      }
    }

    const matchCount = matchingVideos.length + matchingImages.length;
    const hasMatches = matchCount > 0;

    if (!hasMatches) {
      return { sorted: parsedMedia, hasMatches: false, matchCount: 0 };
    }

    // Build sorted array:
    // 1. First matching video only (if any)
    // 2. All matching images
    // 3. Remaining media in original order
    const sorted = [];

    // Add first matching video
    if (matchingVideos.length > 0) {
      sorted.push(matchingVideos[0]);
      // Remaining matching videos go to non-matching to preserve them
      nonMatching.push(...matchingVideos.slice(1));
    }

    // Add all matching images
    sorted.push(...matchingImages);

    // Sort non-matching by original index to preserve order
    nonMatching.sort((a, b) => a.originalIndex - b.originalIndex);
    sorted.push(...nonMatching);

    return { sorted, hasMatches: true, matchCount };
  }

  /**
   * Check if alt text contains any of the keywords (case-insensitive, partial match)
   */
  function matchesKeywords(altText, keywords) {
    if (!altText) return false;

    const lowerAlt = altText.toLowerCase();

    for (const keyword of keywords) {
      if (lowerAlt.includes(keyword.toLowerCase())) {
        return true;
      }
    }

    return false;
  }

  /**
   * Apply the sorted order to a gallery container
   */
  function applySort(container, sortedMedia, elementKey) {
    if (!container) return;

    // Filter to only items that have this element
    const itemsWithElement = sortedMedia.filter(m => m[elementKey]);
    
    if (itemsWithElement.length === 0) return;

    // Hide during sort
    if (config.hideWhileSorting) {
      container.style.visibility = 'hidden';
    }

    // Remove all items
    for (const media of itemsWithElement) {
      if (media[elementKey] && media[elementKey].parentNode === container) {
        media[elementKey].remove();
      }
    }

    // Re-append in sorted order
    for (const media of itemsWithElement) {
      if (media[elementKey]) {
        container.appendChild(media[elementKey]);
      }
    }

    // Show container and scroll to start
    if (config.hideWhileSorting) {
      requestAnimationFrame(() => {
        container.style.visibility = '';
        scrollToStart(container);
      });
    } else {
      scrollToStart(container);
    }
  }

    /**
   * Scroll gallery to the first item
   */
  function scrollToStart(container) {
    // Method 1: Direct scroll reset
    container.scrollLeft = 0;
    
    // Method 2: If it's inside a slider-component, try to use its API
    const sliderComponent = container.closest('slider-component');
    if (sliderComponent && typeof sliderComponent.resetSlider === 'function') {
      sliderComponent.resetSlider();
    }
    
    // Method 3: Click the first thumbnail to sync (if thumbnails exist)
    const firstThumb = document.querySelector(config.thumbnailContainerSelector + ' > ' + config.mediaItemSelector + ':first-child button');
    if (firstThumb && container.classList.contains('product__media-list')) {
      // Small delay to ensure DOM is settled
      setTimeout(() => {
        firstThumb.click();
      }, 50);
    }
  }

  /**
   * Debug logging
   */
  function log(...args) {
    if (config.debug) {
      console.log('[AKZ MediaSorter]', ...args);
    }
  }

  /**
   * Get current category keywords (for debugging/editing)
   */
  function getKeywords() {
    return { ...CATEGORY_KEYWORDS };
  }

  /**
   * Update keywords for a category
   */
  function setKeywords(category, keywords) {
    if (Array.isArray(keywords)) {
      CATEGORY_KEYWORDS[category] = keywords;
    }
  }

  /**
   * Manually trigger sort (useful for SPAs or dynamic content)
   */
  function refresh() {
    sortMedia();
  }

  return {
    init,
    refresh,
    getKeywords,
    setKeywords
  };
})();